import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { adminSession, type AdminAuditEntry } from '@/lib/api';

/**
 * /dashboard/audit — global admin-action audit log.
 *
 * Lists every audit_log row with actor_admin_id set, regardless of
 * which user (if any) it targeted. The per-user audit trail on
 * /dashboard/users/:id is the user-scoped slice; this page is the
 * "what have admins been up to" view. Useful for spot-checking the
 * other admins on the team + for compliance reviews ("show me every
 * tier flip in Q3").
 *
 * Pagination: 50/page. Filter by action prefix (e.g. "admin.user"
 * narrows to user mutations, "admin.billing" to plan-visibility +
 * future payment work).
 */
export function AuditLogPage() {
  const token = adminSession.getToken() ?? '';
  const [page, setPage] = useState(1);
  const [prefix, setPrefix] = useState('');
  const [prefixInput, setPrefixInput] = useState('');
  const pageSize = 50;

  const data = useQuery(
    api.admin.audit.listAdminAuditQ,
    token
      ? { token, page, pageSize, actionPrefix: prefix || undefined }
      : 'skip',
  );

  const feed = data ?? null;
  const loading = data === undefined;

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setPrefix(prefixInput.trim());
  };

  const totalPages = feed ? Math.max(1, Math.ceil(feed.total / pageSize)) : 1;

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
            Audit log
          </h2>
          <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }}>
            {feed ? `${feed.total.toLocaleString()} admin actions on record.` : 'Loading…'}
          </div>
        </div>
        <form onSubmit={onSearch} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', minWidth: 0 }}>
          <input
            type="text"
            value={prefixInput}
            onChange={(e) => setPrefixInput(e.target.value)}
            placeholder="action prefix, e.g. admin.user"
            style={{
              padding: '8px 12px',
              borderRadius: 100,
              border: '1px solid var(--line-strong)',
              fontSize: 13,
              flex: '1 1 220px',
              minWidth: 0,
              fontFamily: 'var(--font-ui)',
              background: 'var(--surface)',
              color: 'var(--ink)',
            }}
          />
          <button type="submit" style={btnFilter}>
            Filter
          </button>
          {prefix && (
            <button
              type="button"
              onClick={() => { setPrefixInput(''); setPrefix(''); setPage(1); }}
              style={btnFilterGhost}
            >
              Clear
            </button>
          )}
        </form>
      </header>

      {loading && !feed ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>Loading…</div>
      ) : !feed || feed.entries.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>
          No admin actions match this filter.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {feed.entries.map((e: AdminAuditEntry) => <AuditRow key={e.id} entry={e} />)}
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, marginTop: 16,
              }}
            >
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}>
                Page {feed.page} / {totalPages}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  style={paginationBtn(page <= 1)}
                >
                  ← Newer
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  style={paginationBtn(page >= totalPages)}
                >
                  Older →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Compact pretty-printer for the after/before json so the row stays
// one-line. Strips obvious envelope fields the user already sees
// elsewhere (method/path/status).
function renderDelta(entry: AdminAuditEntry): string {
  const merged: Record<string, unknown> = { ...(entry.before || {}), ...(entry.after || {}) };
  const keys = Object.keys(merged).filter((k) =>
    !['method', 'path', 'status'].includes(k),
  );
  if (keys.length === 0) return '';
  // Show up to 3 key=val pairs; clip values that get too long.
  return keys.slice(0, 3).map((k) => {
    let v: string = '';
    try { v = JSON.stringify(merged[k]); } catch { v = String(merged[k]); }
    if (v.length > 40) v = v.slice(0, 39) + '…';
    return `${k}=${v}`;
  }).join('  ');
}

function AuditRow({ entry }: { entry: AdminAuditEntry }) {
  const when = new Date(entry.created_at);
  const delta = renderDelta(entry);
  const target = entry.target_type
    ? `${entry.target_type}:${entry.target_id?.slice(0, 8) ?? '—'}`
    : null;
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 13,
      }}
    >
      {/* Row 1 — action label + timestamp aligned right. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 12 }}>
        <span style={{ fontWeight: 600, color: 'var(--ink)', minWidth: 0, overflowWrap: 'anywhere' }}>{entry.action}</span>
        <span
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap', flex: '0 0 auto' }}
          title={when.toISOString()}
        >
          {when.toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })}
        </span>
      </div>
      {/* Row 2 — actor + delta + target + IP, comma/dot separated. */}
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'baseline',
          lineHeight: 1.5,
          overflowWrap: 'anywhere',
        }}
      >
        <span style={{ color: 'var(--ink-2)' }}>
          {entry.actor_email || entry.actor_admin_id.slice(0, 8) + '…'}
        </span>
        {delta && (
          <>
            <Sep />
            <span style={{ color: 'var(--ink-2)' }}>{delta}</span>
          </>
        )}
        {target && (
          <>
            <Sep />
            <span>{target}</span>
          </>
        )}
        {entry.ip && (
          <>
            <Sep />
            <span title={entry.ip} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.ip}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function Sep() {
  return <span style={{ color: 'var(--ink-4, var(--line-strong))' }}>·</span>;
}

const btnFilter: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const btnFilterGhost: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-3)',
  border: '1px solid var(--line-strong)',
  borderRadius: 100,
  padding: '8px 14px',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

function paginationBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--chip)' : 'var(--surface)',
    color: disabled ? 'var(--ink-3)' : 'var(--ink)',
    border: '1px solid var(--line-strong)',
    borderRadius: 100,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-ui)',
    opacity: disabled ? 0.5 : 1,
  };
}
